import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import Select, { createFilter } from 'react-select';
import styles from './AutoReplyRecurringStatus.module.css';

const autoreplyExpirationTimePeriods = [];
const startDate = new Date();
const optionsCount = 49;
const minutesStep = 30;
startDate.setHours(0, 0, 0, 0);

for (let i = 0; i < optionsCount; i += 1) {
  let hours = startDate.getHours();
  const minutes = startDate.getMinutes();
  if (hours === 0 && minutes === 0) {
    hours = 24;
  }
  const formattedDate = startDate.toLocaleString('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
  autoreplyExpirationTimePeriods.push({
    value: formattedDate,
    label: `${formattedDate} `,
  });
  startDate.setMinutes(startDate.getMinutes() + minutesStep - (i === optionsCount - 2 ? 1 : 0));
}

const daysOfTheWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const customAutoReplyDropdownStyles = {
  control: (provided) => ({
    ...provided,
    border: '1px solid #BEBEBE',
    borderRadius: '4px',
  }),
  option: (provided) => ({
    ...provided,
    font: 'normal normal normal 15px/20px Roboto, serif',
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 100, // setting zIndex to 100, because PopperWrapper has zIndex 99
  }),
};

const stringify = (option) => option.label;
const filterOption = createFilter({ stringify });

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const AutoReplyRecurringStatus = observer(function AutoReplyRecurringStatus(props) {
  const { t } = useTranslation();
  const { initialRecurringStatus, onRecurringStatusChange, showDaysErrorMessage } = props;

  const [selectedDays, setSelectedDays] = React.useState(() => {
    if (initialRecurringStatus) {
      const days = initialRecurringStatus.split('; ')[0];

      return days
        .split(', ')
        .map((day) => daysOfTheWeek.findIndex((dayOfWeek) => dayOfWeek.substring(0, 3) === day));
    }

    return [];
  });

  const [startTime, setStartTime] = React.useState(() => {
    if (initialRecurringStatus) {
      const initialStartTime = initialRecurringStatus.split('; ')[1].split(' - ')[0];

      return autoreplyExpirationTimePeriods.find((period) => period.value === initialStartTime);
    }

    return autoreplyExpirationTimePeriods[0];
  });

  const [endTime, setEndTime] = React.useState(() => {
    if (initialRecurringStatus) {
      const initialEndTime = initialRecurringStatus.split('; ')[1].split(' - ')[1];

      return autoreplyExpirationTimePeriods.find((period) => period.value === initialEndTime);
    }

    return autoreplyExpirationTimePeriods[autoreplyExpirationTimePeriods.length - 1];
  });

  const getRecurringStatusString = () => {
    const days = selectedDays.map((index) => daysOfTheWeek[index].slice(0, 3)).join(', ');
    return `${days}; ${startTime.value} - ${endTime.value}`;
  };

  React.useEffect(() => {
    const recurringStatus =
      selectedDays && selectedDays.length > 0 ? getRecurringStatusString() : null;

    onRecurringStatusChange(recurringStatus);
  });

  const onDayClicked = (index) => {
    if (selectedDays.includes(index)) {
      setSelectedDays(selectedDays.filter((day) => day !== index));
    } else {
      setSelectedDays([...selectedDays, index]);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.daysContainer}>
        {daysOfTheWeek.map((day, index) => (
          <button
            type="button"
            className={selectedDays.includes(index) ? styles.dayButtonSelected : styles.dayButton}
            onClick={() => onDayClicked(index)}
            key={day}
          >
            {t(`AutoReplyDaysInitials.${day}`)}
          </button>
        ))}
      </div>
      {showDaysErrorMessage && !selectedDays?.length ? (
        <span className={styles.errorLabel}>
          {t('AutoReplyRecurringStatus.PleaseSelectActiveDays')}
        </span>
      ) : null}
      <div className={styles.dropdownsContainer}>
        <div className={styles.dropdownContainer}>
          <span className={styles.label}>{t('CustomAwayMessages.Starts')}</span>
          <Select
            styles={customAutoReplyDropdownStyles}
            className={styles.timeSelectDropdown}
            filterOption={filterOption}
            value={startTime}
            onChange={setStartTime}
            options={autoreplyExpirationTimePeriods.filter(
              (option) => option.value !== endTime.value
            )}
            defaultValue={autoreplyExpirationTimePeriods[0]}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <div className={styles.dropdownContainer}>
          <span className={styles.label}>{t('CustomAwayMessages.Ends')}</span>
          <Select
            styles={customAutoReplyDropdownStyles}
            className={styles.timeSelectDropdown}
            filterOption={filterOption}
            value={endTime}
            onChange={setEndTime}
            options={autoreplyExpirationTimePeriods.filter(
              (option) => option.value !== startTime.value
            )}
            defaultValue={autoreplyExpirationTimePeriods[0]}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>
      </div>
    </div>
  );
});
AutoReplyRecurringStatus.whyDidYouRender = true;
AutoReplyRecurringStatus.displayName = 'AutoReplyRecurringStatus';

export default React.memo(AutoReplyRecurringStatus);
